/**
 * CAOS-595 — the rich format's editor: the toolbar, the surface a person types into, and the commands
 * behind the buttons. It is internal to `<caos-field-renderer>`, which draws it for a Text field in the
 * `rich` format. It is not an element of its own, because reading and editing a rich value are one
 * renderer in two modes; two components would drift.
 *
 * WHAT IT STORES. Whatever the surface holds, run through {@link sanitizeRichText}. The surface itself is
 * a browser editing region, and a browser adds markup of its own while a person types (a `div` for a new
 * line, a `span` carrying a style), so a top-level `div` is read as a paragraph before sanitising and
 * anything else outside the allow-list is dropped there. Pasted and dropped content goes through the same
 * sanitiser on the way in.
 *
 * THE COMMANDS are plain functions over a DOM `Range` inside the surface, exported so they can be
 * exercised directly; the buttons only find the range and call them.
 *   • bold / italic / underline / strikethrough wrap the selection. With the selection already inside
 *     that format, the button removes it from the whole formatted run.
 *   • bulleted / numbered list turns the blocks the selection touches into one list. When every block is
 *     already that kind of list, it turns them back into paragraphs.
 *   • link asks for an address, and applies it only if the link rules in `rich-text.ts` accept it.
 *   • clear formatting replaces the selection with its plain text.
 *
 * THE EXTENSION SEAM. A package adds buttons with a `rich-text` capability claim at the `toolbar` seam;
 * whoever mounts the renderer reads those claims and hands them over as
 * {@link RichTextToolbarContribution}s. Each carries exactly one action:
 *   • `insert` — markup placed at the cursor, sanitised by the same allow-list, so a contribution can
 *     never put into a record what the editor itself could not;
 *   • `command` — a `caos-rich-text-command` event, raised from the renderer for the package's own
 *     component to handle, carrying the command name, the button, its package and the selected text.
 * Contributed buttons sit after the platform's own, grouped by their declared `group`, in `order`.
 */
import { makeIcon, isIconName, type IconName } from './components/icons.js';
import { richTextFragment, sanitizeRichText, safeLinkHref, isRichTextEmpty, RICH_TEXT_LINK_REL } from './rich-text.js';

/** One toolbar button a package contributes, as read off its capability claim. */
export interface RichTextToolbarContribution {
  /** Unique within the toolbar. */
  readonly key: string;
  /** What the button is called; also its accessible name. */
  readonly label: string;
  /** A glyph from the shared icon vocabulary; absent or unknown ⇒ the label is shown instead. */
  readonly icon?: string;
  /** Buttons naming the same group sit together; absent ⇒ the contributing package's own group. */
  readonly group?: string;
  /** Position among contributed buttons, lower first. */
  readonly order?: number;
  /** The package the button came from. */
  readonly sourceKey?: string;
  /** Markup placed at the cursor, sanitised. Exactly one of `insert` and `command`. */
  readonly insert?: string;
  /** The name raised in a `caos-rich-text-command` event. Exactly one of `insert` and `command`. */
  readonly command?: string;
}

/** What a `caos-rich-text-command` event carries. */
export interface RichTextCommandDetail {
  readonly command: string;
  readonly key: string;
  readonly sourceKey?: string;
  readonly selectedText: string;
}

export type RichTextInlineCommand = 'bold' | 'italic' | 'underline' | 'strike';
export type RichTextListCommand = 'bulleted' | 'numbered';
export type RichTextCommand = RichTextInlineCommand | RichTextListCommand | 'link' | 'clear';

/** The platform's own toolbar, in groups. */
export const RICH_TEXT_TOOLBAR: ReadonlyArray<{
  readonly group: string;
  readonly buttons: ReadonlyArray<{ readonly command: RichTextCommand; readonly label: string; readonly icon: IconName }>;
}> = [
  {
    group: 'format',
    buttons: [
      { command: 'bold', label: 'Bold', icon: 'bold' },
      { command: 'italic', label: 'Italic', icon: 'italic' },
      { command: 'underline', label: 'Underline', icon: 'underline' },
      { command: 'strike', label: 'Strikethrough', icon: 'strikethrough' },
    ],
  },
  {
    group: 'lists',
    buttons: [
      { command: 'bulleted', label: 'Bulleted list', icon: 'list' },
      { command: 'numbered', label: 'Numbered list', icon: 'list-ordered' },
    ],
  },
  {
    group: 'insert',
    buttons: [
      { command: 'link', label: 'Link', icon: 'link' },
      { command: 'clear', label: 'Clear formatting', icon: 'remove-formatting' },
    ],
  },
];

const INLINE_TAGS: Readonly<Record<RichTextInlineCommand, readonly string[]>> = {
  bold: ['strong', 'b'],
  italic: ['em', 'i'],
  underline: ['u'],
  strike: ['s'],
};
const FORMATTING_TAGS = ['strong', 'b', 'em', 'i', 'u', 's', 'a', 'code'];
const TOP_BLOCKS = ['p', 'h2', 'h3', 'blockquote', 'pre', 'ul', 'ol'];

function isElement(node: Node | null, tags?: readonly string[]): node is Element {
  return !!node && node.nodeType === 1 && (!tags || tags.includes((node as Element).localName));
}

/** The nearest element named in `tags` holding `node`, without leaving `root`. */
function closestWithin(node: Node | null, tags: readonly string[], root: Node): Element | null {
  for (let n: Node | null = node; n && n !== root; n = n.parentNode) if (isElement(n, tags)) return n;
  return null;
}

function holds(root: Node, range: Range): boolean {
  return root === range.commonAncestorContainer || root.contains(range.commonAncestorContainer);
}

function unwrap(el: Element): void {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

/** Bold, italic, underline or strikethrough over the range; see the header for how it toggles off. */
export function toggleInline(root: HTMLElement, range: Range, command: RichTextInlineCommand): void {
  if (!holds(root, range)) return;
  const tags = INLINE_TAGS[command];
  const start = closestWithin(range.startContainer, tags, root);
  if (start && start === closestWithin(range.endContainer, tags, root)) {
    unwrap(start);
    return;
  }
  if (range.collapsed) return;
  const content = range.extractContents();
  content.querySelectorAll(tags.join(',')).forEach(unwrap);
  const wrapper = root.ownerDocument.createElement(tags[0]!);
  wrapper.append(content);
  range.insertNode(wrapper);
  range.selectNodeContents(wrapper);
}

/** Loose text and inline elements sitting directly in `root` gathered into paragraphs. */
function paragraphsForLooseContent(root: HTMLElement): void {
  const doc = root.ownerDocument;
  let run: Node[] = [];
  const flush = (before: Node | null): void => {
    if (run.length === 0) return;
    const onlyWhitespace = run.every((n) => n.nodeType === 3 && (n.nodeValue ?? '').trim() === '');
    if (!onlyWhitespace) {
      const p = doc.createElement('p');
      root.insertBefore(p, before);
      for (const n of run) p.appendChild(n);
    }
    run = [];
  };
  for (const node of Array.from(root.childNodes)) {
    if (isElement(node, [...TOP_BLOCKS, 'div'])) {
      flush(node);
      if (node.localName === 'div') {
        const p = doc.createElement('p');
        root.insertBefore(p, node);
        while (node.firstChild) p.appendChild(node.firstChild);
        root.removeChild(node);
      }
    } else {
      run.push(node);
    }
  }
  flush(null);
}

/** The child of `root` that holds `node`. */
function topLevelOf(root: HTMLElement, node: Node): Element | null {
  let n: Node | null = node;
  while (n && n.parentNode !== root) n = n.parentNode;
  return isElement(n) ? n : null;
}

/** A bulleted or numbered list over the blocks the range touches; see the header for how it toggles off. */
export function toggleList(root: HTMLElement, range: Range, command: RichTextListCommand): void {
  if (!holds(root, range)) return;
  const doc = root.ownerDocument;
  const listTag = command === 'bulleted' ? 'ul' : 'ol';
  const pick = (container: Node, offset: number): Node =>
    container === root ? (root.childNodes[Math.min(offset, root.childNodes.length - 1)] ?? root) : container;
  const startNode = pick(range.startContainer, range.startOffset);
  const endNode = pick(range.endContainer, Math.max(0, range.endOffset - 1));
  if (root.childNodes.length === 0) {
    const list = doc.createElement(listTag);
    const item = doc.createElement('li');
    item.append(doc.createElement('br'));
    list.append(item);
    root.append(list);
    return;
  }
  paragraphsForLooseContent(root);
  const first = topLevelOf(root, startNode);
  const last = topLevelOf(root, endNode) ?? first;
  if (!first || !last) return;
  const blocks: Element[] = [];
  for (let n: Node | null = first; n; n = n.nextSibling) {
    if (isElement(n)) blocks.push(n);
    if (n === last) break;
  }
  if (blocks.every((b) => b.localName === listTag)) {
    for (const list of blocks) {
      for (const item of Array.from(list.children)) {
        const p = doc.createElement('p');
        while (item.firstChild) p.appendChild(item.firstChild);
        root.insertBefore(p, list);
      }
      root.removeChild(list);
    }
    return;
  }
  const list = doc.createElement(listTag);
  root.insertBefore(list, blocks[0]!);
  for (const block of blocks) {
    if (block.localName === 'ul' || block.localName === 'ol') {
      for (const item of Array.from(block.children)) list.appendChild(item);
    } else {
      const item = doc.createElement('li');
      while (block.firstChild) item.appendChild(block.firstChild);
      list.appendChild(item);
    }
    root.removeChild(block);
  }
}

/** A link over the range, or a new link at the cursor. Returns false when the address is refused. */
export function applyLink(root: HTMLElement, range: Range, address: string): boolean {
  const href = safeLinkHref(address);
  if (href === null || !holds(root, range)) return false;
  const existing = closestWithin(range.startContainer, ['a'], root);
  if (existing && existing === closestWithin(range.endContainer, ['a'], root)) {
    existing.setAttribute('href', href);
    existing.setAttribute('rel', RICH_TEXT_LINK_REL);
    return true;
  }
  const link = root.ownerDocument.createElement('a');
  link.setAttribute('href', href);
  link.setAttribute('rel', RICH_TEXT_LINK_REL);
  if (range.collapsed) {
    link.textContent = href.replace(/^mailto:/, '');
  } else {
    const content = range.extractContents();
    content.querySelectorAll('a').forEach(unwrap);
    link.append(content);
  }
  range.insertNode(link);
  range.selectNodeContents(link);
  return true;
}

/** The link the range sits in, removed, keeping its text. */
export function removeLink(root: HTMLElement, range: Range): void {
  const link = closestWithin(range.startContainer, ['a'], root);
  if (link) unwrap(link);
}

/** `text`, moved out of the formatting element `ancestor`, which is split around it. */
function liftOut(text: Text, ancestor: Element): void {
  const doc = ancestor.ownerDocument;
  const parent = ancestor.parentNode!;
  const tail = doc.createRange();
  tail.setStartAfter(text);
  tail.setEnd(ancestor, ancestor.childNodes.length);
  const after = ancestor.cloneNode(false) as Element;
  after.append(tail.extractContents());
  const middle = doc.createRange();
  middle.setStartBefore(text);
  middle.setEnd(ancestor, ancestor.childNodes.length);
  const plain = doc.createTextNode(middle.extractContents().textContent ?? '');
  parent.insertBefore(plain, ancestor.nextSibling);
  if ((after.textContent ?? '') !== '') parent.insertBefore(after, plain.nextSibling);
  if ((ancestor.textContent ?? '') === '') parent.removeChild(ancestor);
  if (plain !== text) text.replaceData(0, text.length, plain.data); // keep the caller's handle meaningful
}

/** The selection replaced with its plain text, outside any formatting. */
export function clearFormatting(root: HTMLElement, range: Range): void {
  if (!holds(root, range) || range.collapsed) return;
  const doc = root.ownerDocument;
  let text = doc.createTextNode(range.toString());
  range.deleteContents();
  range.insertNode(text);
  for (let format = closestWithin(text, FORMATTING_TAGS, root); format; format = closestWithin(text, FORMATTING_TAGS, root)) {
    const parent = format.parentNode!;
    const index = Array.prototype.indexOf.call(parent.childNodes, format) as number;
    liftOut(text, format);
    // The lifted text is the node after the remains of `format` (or in its place when it emptied).
    const candidate = parent.childNodes[index + ((parent.childNodes[index] as Node | undefined) && isElement(parent.childNodes[index]!, [format.localName]) ? 1 : 0)];
    if (!candidate || candidate.nodeType !== 3) break;
    text = candidate as Text;
  }
  range.selectNodeContents(text);
}

/** Sanitised markup placed at the range, replacing any selection. */
export function insertMarkup(root: HTMLElement, range: Range, html: string): void {
  if (!holds(root, range)) return;
  const fragment = richTextFragment(html, root.ownerDocument);
  const last = fragment.lastChild;
  range.deleteContents();
  range.insertNode(fragment);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
  }
}

/** Plain text as rich content: each line its own text, with a line break between. */
function plainTextMarkup(text: string): string {
  const holder = document.createElement('div');
  text.split(/\r\n|\r|\n/).forEach((line, i) => {
    if (i > 0) holder.append(document.createElement('br'));
    holder.append(document.createTextNode(line));
  });
  return holder.innerHTML;
}

/** What the surface holds, as the value to store: top-level `div`s read as paragraphs, then sanitised. */
export function editorValue(surface: HTMLElement): string {
  const copy = surface.cloneNode(true) as HTMLElement;
  for (const div of Array.from(copy.children)) {
    if (div.localName !== 'div') continue;
    const p = copy.ownerDocument.createElement('p');
    while (div.firstChild) p.appendChild(div.firstChild);
    copy.replaceChild(p, div);
  }
  const html = sanitizeRichText(copy.innerHTML);
  return isRichTextEmpty(html) ? '' : html;
}

/** The selection inside `surface`, looking through the shadow root it is drawn in. */
function selectionRange(surface: HTMLElement): Range | null {
  const root = surface.getRootNode() as Document | ShadowRoot;
  const selection =
    (typeof (root as unknown as { getSelection?: () => Selection | null }).getSelection === 'function'
      ? (root as unknown as { getSelection: () => Selection | null }).getSelection()
      : null) ?? document.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const composed = (selection as unknown as { getComposedRanges?: (...args: unknown[]) => StaticRange[] }).getComposedRanges;
  if (typeof composed === 'function' && typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) {
    let ranges: StaticRange[] = [];
    try {
      ranges = composed.call(selection, { shadowRoots: [root] });
    } catch {
      try {
        ranges = composed.call(selection, root);
      } catch {
        ranges = [];
      }
    }
    const found = ranges[0];
    if (found) {
      const range = document.createRange();
      range.setStart(found.startContainer, found.startOffset);
      range.setEnd(found.endContainer, found.endOffset);
      if (holds(surface, range)) return range;
    }
  }
  const range = selection.getRangeAt(0);
  return holds(surface, range) ? range : null;
}

export interface RichTextEditorOptions {
  readonly label?: string;
  readonly required?: boolean;
  readonly value: unknown;
  readonly contributions: readonly RichTextToolbarContribution[];
  /**
   * Called with the stored value when the person leaves the editor having changed it. A toolbar press
   * does not commit: it keeps focus in the editor, and a record saved on every press would save a
   * half-made change.
   */
  readonly onCommit: (value: string) => void;
}

/** The editor element: a toolbar and a surface. It raises `change` when the person leaves it having changed the value. */
export type RichTextEditorElement = HTMLDivElement & { readonly value: string };

let editorCount = 0;

/** A contribution the toolbar can draw: named, with exactly one action. */
function usable(c: RichTextToolbarContribution): boolean {
  const actions = (typeof c.insert === 'string' ? 1 : 0) + (typeof c.command === 'string' && c.command !== '' ? 1 : 0);
  return typeof c.key === 'string' && c.key !== '' && typeof c.label === 'string' && c.label !== '' && actions === 1;
}

/** Contributed buttons in the order they are drawn: by `order`, then package, then key; grouped by first appearance. */
export function contributedGroups(
  contributions: readonly RichTextToolbarContribution[],
): Array<{ group: string; buttons: RichTextToolbarContribution[] }> {
  const sorted = contributions.filter(usable).slice().sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) ||
      (a.sourceKey ?? '').localeCompare(b.sourceKey ?? '') ||
      a.key.localeCompare(b.key),
  );
  const groups = new Map<string, RichTextToolbarContribution[]>();
  for (const c of sorted) {
    const group = c.group ?? c.sourceKey ?? c.key;
    const list = groups.get(group) ?? [];
    if (list.some((existing) => existing.key === c.key) || [...groups.values()].some((l) => l.some((e) => e.key === c.key))) continue;
    list.push(c);
    groups.set(group, list);
  }
  return [...groups].map(([group, buttons]) => ({ group, buttons }));
}

export function richTextEditor(options: RichTextEditorOptions): RichTextEditorElement {
  const id = `caos-rich-${++editorCount}`;
  const wrap = document.createElement('div') as RichTextEditorElement;
  wrap.className = 'rich-editor';
  wrap.setAttribute('part', 'control');

  if (options.label) {
    const label = document.createElement('span');
    label.className = 'rich-label';
    label.id = `${id}-label`;
    label.textContent = options.label;
    if (options.required) {
      const req = document.createElement('span');
      req.className = 'req';
      req.setAttribute('aria-hidden', 'true');
      req.textContent = '*';
      label.append(req);
    }
    wrap.append(label);
  }

  const toolbar = document.createElement('div');
  toolbar.className = 'rich-toolbar';
  toolbar.setAttribute('role', 'toolbar');
  toolbar.setAttribute('aria-label', options.label ? `${options.label} formatting` : 'Formatting');
  toolbar.setAttribute('part', 'rich-toolbar');

  const surface = document.createElement('div');
  surface.className = 'rich-surface rich';
  surface.setAttribute('contenteditable', 'true');
  surface.setAttribute('role', 'textbox');
  surface.setAttribute('aria-multiline', 'true');
  surface.setAttribute('part', 'rich-surface');
  if (options.label) surface.setAttribute('aria-labelledby', `${id}-label`);
  if (options.required) surface.setAttribute('aria-required', 'true');
  surface.append(richTextFragment(options.value));

  const linkRow = document.createElement('div');
  linkRow.className = 'rich-link';
  linkRow.hidden = true;
  const linkInput = document.createElement('input');
  linkInput.type = 'url';
  linkInput.placeholder = 'https://';
  linkInput.setAttribute('aria-label', 'Link address');
  const linkApply = document.createElement('button');
  linkApply.type = 'button';
  linkApply.textContent = 'Apply';
  const linkRemove = document.createElement('button');
  linkRemove.type = 'button';
  linkRemove.textContent = 'Remove link';
  const linkCancel = document.createElement('button');
  linkCancel.type = 'button';
  linkCancel.textContent = 'Cancel';
  const linkMessage = document.createElement('span');
  linkMessage.className = 'rich-link-message';
  linkMessage.setAttribute('role', 'alert');
  linkRow.append(linkInput, linkApply, linkRemove, linkCancel, linkMessage);

  let saved: Range | null = null;
  const remember = (): void => {
    const range = selectionRange(surface);
    if (range) saved = range.cloneRange();
  };
  /** The range to act on: the live selection in the surface, else the last one it had, else its end. */
  const workingRange = (): Range => {
    const live = selectionRange(surface);
    if (live) return live;
    if (saved && holds(surface, saved)) return saved;
    const end = document.createRange();
    end.selectNodeContents(surface);
    end.collapse(false);
    return end;
  };
  const select = (range: Range): void => {
    saved = range.cloneRange();
    try {
      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    } catch {
      /* a selection the browser will not take is only a lost highlight */
    }
  };

  let committed = editorValue(surface);
  Object.defineProperty(wrap, 'value', { get: () => editorValue(surface) });
  const commit = (): void => {
    const next = editorValue(surface);
    if (next === committed) return;
    committed = next;
    options.onCommit(next);
    wrap.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const pressed: Array<{ button: HTMLButtonElement; tags: readonly string[] }> = [];
  const refreshPressed = (): void => {
    const range = selectionRange(surface);
    for (const { button, tags } of pressed) {
      const on = !!range && !!closestWithin(range.startContainer, tags, surface);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };

  const openLink = (): void => {
    remember();
    const range = workingRange();
    const current = closestWithin(range.startContainer, ['a'], surface);
    linkInput.value = current?.getAttribute('href') ?? '';
    linkRemove.hidden = !current;
    linkMessage.textContent = '';
    linkRow.hidden = false;
    linkInput.focus();
  };
  const closeLink = (): void => {
    linkRow.hidden = true;
    linkMessage.textContent = '';
    surface.focus();
  };
  linkApply.addEventListener('click', () => {
    const range = saved && holds(surface, saved) ? saved : workingRange();
    if (!applyLink(surface, range, linkInput.value)) {
      linkMessage.textContent = 'Use a full web address (https://…) or an email address (mailto:…).';
      return;
    }
    select(range);
    closeLink();
  });
  linkRemove.addEventListener('click', () => {
    const range = saved && holds(surface, saved) ? saved : workingRange();
    removeLink(surface, range);
    closeLink();
  });
  linkCancel.addEventListener('click', closeLink);
  linkInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      linkApply.click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeLink();
    }
  });

  const addButton = (group: HTMLElement, label: string, icon: string | undefined, run: () => void): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rich-button';
    button.setAttribute('aria-label', label);
    button.title = label;
    if (icon && isIconName(icon)) {
      const glyph = document.createElement('span');
      glyph.className = 'glyph';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.append(makeIcon(icon));
      button.append(glyph);
    } else {
      button.textContent = label;
    }
    // Keep the selection in the surface: a pressed button would otherwise take focus, and the selection with it.
    button.addEventListener('mousedown', (e) => e.preventDefault());
    button.addEventListener('click', run);
    group.append(button);
    return button;
  };

  const run = (command: RichTextCommand): void => {
    if (command === 'link') {
      openLink();
      return;
    }
    const range = workingRange();
    if (command === 'bulleted' || command === 'numbered') toggleList(surface, range, command);
    else if (command === 'clear') clearFormatting(surface, range);
    else toggleInline(surface, range, command);
    select(range);
    surface.focus();
    refreshPressed();
  };

  for (const { group, buttons } of RICH_TEXT_TOOLBAR) {
    const el = document.createElement('div');
    el.className = 'rich-group';
    el.setAttribute('role', 'group');
    el.dataset['group'] = group;
    for (const b of buttons) {
      const button = addButton(el, b.label, b.icon, () => run(b.command));
      if (b.command in INLINE_TAGS) {
        button.setAttribute('aria-pressed', 'false');
        pressed.push({ button, tags: INLINE_TAGS[b.command as RichTextInlineCommand] });
      }
    }
    toolbar.append(el);
  }
  for (const { group, buttons } of contributedGroups(options.contributions)) {
    const el = document.createElement('div');
    el.className = 'rich-group contributed';
    el.setAttribute('role', 'group');
    el.dataset['group'] = group;
    for (const c of buttons) {
      addButton(el, c.label, c.icon, () => {
        const range = workingRange();
        if (typeof c.insert === 'string') {
          insertMarkup(surface, range, c.insert);
          select(range);
          surface.focus();
          return;
        }
        const detail: RichTextCommandDetail = {
          command: c.command!,
          key: c.key,
          ...(c.sourceKey ? { sourceKey: c.sourceKey } : {}),
          selectedText: range.toString(),
        };
        wrap.dispatchEvent(new CustomEvent<RichTextCommandDetail>('caos-rich-text-command', { bubbles: true, composed: true, detail }));
      });
    }
    toolbar.append(el);
  }

  const insertTransfer = (data: DataTransfer | null): void => {
    if (!data) return;
    const html = data.getData('text/html');
    const markup = html !== '' ? html : plainTextMarkup(data.getData('text/plain'));
    const range = workingRange();
    insertMarkup(surface, range, markup);
    select(range);
  };
  surface.addEventListener('paste', (e) => {
    e.preventDefault();
    insertTransfer((e as ClipboardEvent).clipboardData);
  });
  surface.addEventListener('drop', (e) => {
    e.preventDefault();
    insertTransfer((e as DragEvent).dataTransfer);
  });
  for (const type of ['keyup', 'mouseup', 'input']) {
    surface.addEventListener(type, () => {
      remember();
      refreshPressed();
    });
  }
  wrap.addEventListener('focusout', (e) => {
    const next = (e as FocusEvent).relatedTarget as Node | null;
    if (next && wrap.contains(next)) return;
    commit();
  });
  // Escape inside the link row is the row's own; everywhere else it belongs to whoever holds the editor.

  wrap.append(toolbar, linkRow, surface);
  Object.defineProperty(wrap, 'focus', { value: () => surface.focus() });
  return wrap;
}

/** The rich format's styles, shared by the reader and the editor. */
export const RICH_TEXT_STYLES = `
  .rich { font-family: var(--caos-font); font-size: var(--caos-text-body); line-height: 1.5; color: var(--caos-color-text); }
  .rich > :first-child { margin-top: 0; }
  .rich > :last-child { margin-bottom: 0; }
  .rich p, .rich ul, .rich ol, .rich blockquote, .rich pre { margin: 0 0 var(--caos-space-2); }
  .rich h2 { font-size: 18px; /* no scale step between 28px and 14px; see CAOS-599 */ font-weight: 600; margin: var(--caos-space-3) 0 var(--caos-space-2); }
  .rich h3 { font-size: var(--caos-text-body); font-weight: 600; margin: var(--caos-space-3) 0 var(--caos-space-1); }
  .rich ul, .rich ol { padding-left: var(--caos-space-5, 24px); }
  .rich blockquote { padding-left: var(--caos-space-3); border-left: 2px solid var(--caos-color-border); color: var(--caos-color-text-muted); }
  .rich code, .rich pre { font-family: var(--caos-font-mono); font-size: 0.92em; }
  .rich pre { white-space: pre-wrap; padding: var(--caos-space-2); background: var(--caos-color-surface-2); border-radius: var(--caos-radius); }
  .rich a { color: var(--caos-color-accent); text-decoration: underline; text-underline-offset: 2px; }

  .rich-editor { display: flex; flex-direction: column; gap: var(--caos-space-1); }
  .rich-label { font-family: var(--caos-font); font-size: var(--caos-text-body); font-weight: 600; color: var(--caos-color-text-muted); }
  .rich-label .req { color: var(--caos-color-danger); margin-left: 2px; }
  .rich-toolbar {
    display: flex; flex-wrap: wrap; gap: var(--caos-space-2); padding: var(--caos-space-1);
    border: 1px solid var(--caos-color-border); border-bottom: none;
    border-radius: var(--caos-radius) var(--caos-radius) 0 0; background: var(--caos-color-surface);
  }
  .rich-group { display: flex; gap: 2px; }
  .rich-group + .rich-group { padding-left: var(--caos-space-2); border-left: 1px solid var(--caos-color-border); }
  .rich-button {
    display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 6px;
    border: 1px solid transparent; border-radius: var(--caos-radius); background: transparent; color: var(--caos-color-text);
    font-family: var(--caos-font); font-size: var(--caos-text-row); cursor: pointer;
  }
  .rich-button:hover { background: var(--caos-color-surface-2); }
  .rich-button[aria-pressed='true'] { border-color: var(--caos-color-accent); color: var(--caos-color-accent); }
  .rich-button:focus-visible { outline: 2px solid var(--caos-color-accent); outline-offset: 1px; }
  .rich-button .glyph { display: inline-flex; width: 16px; height: 16px; }
  .rich-button .glyph svg { width: 100%; height: 100%; }
  .rich-link { display: flex; flex-wrap: wrap; align-items: center; gap: var(--caos-space-2); padding: var(--caos-space-1); border: 1px solid var(--caos-color-border); border-bottom: none; }
  .rich-link input { flex: 1 1 200px; min-height: 28px; padding: 0 var(--caos-space-2); border: 1px solid var(--caos-color-border); border-radius: var(--caos-radius); font: inherit; }
  .rich-link-message { flex-basis: 100%; color: var(--caos-color-danger-text, var(--caos-color-danger)); font-size: var(--caos-text-row); }
  .rich-link-message:empty { display: none; }
  .rich-surface {
    min-height: 120px; padding: var(--caos-space-3) 14px; overflow-wrap: anywhere;
    border: 1px solid var(--caos-color-border); border-radius: 0 0 var(--caos-radius) var(--caos-radius);
    background: var(--caos-color-surface-2); outline: none;
  }
  .rich-surface:focus-visible, .rich-surface:focus { border-color: var(--caos-color-accent); box-shadow: 0 0 0 1px var(--caos-color-accent); }
`;
